// src/components/HomesShowcase.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import "./HomesShowcase.css";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

export default function HomesShowcase({
  homes = [],
  title: propTitle,
  subtitle: propSubtitle,
  fontStyles,
  sectionStyles = {},
}) {
  const isMobile = useIsMobile();
  const trackRef = useRef(null);

  const [title, setTitle] = useState(propTitle || "");
  const [subtitle, setSubtitle] = useState(propSubtitle || "");

  // NEW → Read dynamic footer background color
  const [footerColor, setFooterColor] = useState("#111827");

  useEffect(() => {
    const off = onValue(ref(db, "footerconfig/backgroundcolor"), (snap) => {
      if (snap.exists()) {
        setFooterColor(snap.val());
      }
    });
    return () => off();
  }, []);

  // Load title/subtitle if not passed
  useEffect(() => {
    if (propTitle || propSubtitle) return;

    const sectionRef = ref(db, "homepageconfig/homesSection");
    const unsub = onValue(sectionRef, (snap) => {
      const data = snap.val();
      if (data) {
        setTitle(data.title || "");
        setSubtitle(data.subtitle || "");
      }
    });
    return () => unsub();
  }, [propTitle, propSubtitle]);

  // Arrows scroll function
  const scrollByCards = (dir = 1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(".home-card");
    const shift = (card?.clientWidth || 320) + 18;

    el.scrollBy({ left: dir * shift, behavior: "smooth" });
  };

  // Auto-slide every 2 seconds
  useEffect(() => {
    const el = trackRef.current;
    if (!el || homes.length <= 1) return;

    const card = el.querySelector(".home-card");
    const shift = (card?.clientWidth || 320) + 18;

    const timer = setInterval(() => {
      const maxScroll = el.scrollWidth - el.clientWidth;

      if (el.scrollLeft + shift + 5 >= maxScroll) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: shift, behavior: "smooth" });
      }
    }, 2000);

    return () => clearInterval(timer);
  }, [homes]);

  if (!homes || homes.length === 0) return null;

  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont = fontStyles?.sectionDescription || fontStyles?.subHeading || {};

  const sizeOverrides = sectionStyles?.fontSizes || {};

  const titleStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.heading || sectionTitleFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 700,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color: sectionStyles?.headingColor || sectionTitleFont?.color || undefined,
  };

  const subtitleStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.subHeading || sectionDescFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color: sectionStyles?.subHeadingColor || sectionDescFont?.color || undefined,
  };

  const captionStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 600,
    lineHeight: fontStyles?.content?.lineHeight || "1.4",
    color: sectionStyles?.contentColor || fontStyles?.content?.color || undefined,
  };

  return (
    <section className="homes-wrap">
      {(title || subtitle) && (
        <div className="homes-header">
          {title && <h2 style={titleStyle}>{title}</h2>}
          {subtitle && <p style={subtitleStyle}>{subtitle}</p>}
        </div>
      )}

      <div className="homes-viewport">
        <button className="homes-arrow left" onClick={() => scrollByCards(-1)}>
          ‹
        </button>

        <div className="homes-track" ref={trackRef}>
          {homes.map((h, i) => (
            <Link className="home-card" to={h.link || "#"} key={i}>
              <figure className="home-figure">
                <img src={h.image} alt={h.title || h.name || ""} />

                {(h.title || h.name) && (
                  <figcaption className="home-caption" style={captionStyle}>
                    {h.title || h.name}
                  </figcaption>
                )}
              </figure>
            </Link>
          ))}
        </div>

        <button className="homes-arrow right" onClick={() => scrollByCards(1)}>
          ›
        </button>
      </div>

      <div className="homes-pager">
        <span className="homes-pager-dot"></span>
      </div>

      <div className="homes-cta-wrap">
        <Link
          to="/contact"
          className="homes-cta"
          style={{ backgroundColor: footerColor }}
        >
          GET FREE QUOTE
        </Link>
      </div>
    </section>
  );
}
